import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  IconButton,
  Box,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const ConfirmationDialog = ({
  open,
  handleClose,
  handleConfirm,
  title,
  content,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  loading = false,
  severity = 'error', // 'error' | 'warning' | 'info' | 'success'
  showCloseIcon = true
}) => {
  const theme = useTheme();

  // Color mapping based on severity
  const severityColors = {
    error: theme.palette.info.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
    success: theme.palette.success.main,
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.85) !important', // Darker overlay with !important
          opacity: '1 !important', // Force opacity
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0,0,0,0.3)', // Enhanced shadow for better depth
          position: 'relative',
          zIndex: 1300, // Ensure dialog is above the backdrop
        }
      }}
    >
      {/* Header with colored accent */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: severityColors[severity],
          color: 'white',
          px: 3,
          py: 2,
          position: 'relative'
        }}
      >
        <DialogTitle
          sx={{
            color: 'inherit',
            p: 0,
            fontSize: '1.25rem',
            fontWeight: 600
          }}
        >
          {title}
        </DialogTitle>
        
        {showCloseIcon && (
          <IconButton
            onClick={handleClose}
            sx={{
              color: 'inherit',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Content area */}
      <DialogContent sx={{ py: 3, px: 3 }}>
        <DialogContentText
          sx={{
            color: 'text.primary',
            fontSize: '1rem',
            lineHeight: 1.6
          }}
        >
          {content}
        </DialogContentText>
      </DialogContent>

      {/* Action buttons */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            px: 3,
            py: 1,
            borderRadius: '8px',
            fontWeight: 500,
            borderWidth: '2px',
            '&:hover': {
              borderWidth: '2px'
            }
          }}
          disabled={loading}
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          sx={{
            textTransform: 'none',
            px: 3,
            py: 1,
            borderRadius: '8px',
            fontWeight: 500,
            bgcolor: severityColors[severity],
            '&:hover': {
              bgcolor: severityColors[severity],
              opacity: 0.9
            }
          }}
          disabled={loading}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} color="inherit" />
              {confirmText}...
            </Box>
          ) : (
            confirmText
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;